import { onScopeDispose, ref, shallowRef, watch } from 'vue'
import { sponsorExpiryTime, type SponsorAd } from '@/domain/sponsors'

type SponsorLoader = (options: { signal: AbortSignal }) => Promise<SponsorAd[]>

export function useSponsorList(load: SponsorLoader, enabled: () => boolean) {
  const ads = shallowRef<readonly SponsorAd[]>([])
  const loading = ref(false)
  let request: AbortController | undefined
  let expiryTimer: ReturnType<typeof setTimeout> | undefined
  function clearExpiry() { clearTimeout(expiryTimer); expiryTimer = undefined }
  function expireAds() {
    clearExpiry()
    const now = Date.now()
    ads.value = ads.value.filter(ad => (sponsorExpiryTime(ad.expiresAt) ?? 0) > now)
    if (ads.value.length) {
      const next = Math.min(...ads.value.map(ad => sponsorExpiryTime(ad.expiresAt)!))
      // Browsers cap a timeout at about 24.8 days; recheck long-lived ads then.
      expiryTimer = setTimeout(expireAds, Math.min(Math.max(1, next - now), 2_147_483_647))
    }
  }
  const stop = watch(enabled, async active => {
    request?.abort(); clearExpiry()
    ads.value = []; loading.value = active
    if (!active) return
    const controller = new AbortController()
    request = controller
    try {
      const result = await load({ signal: controller.signal })
      if (!controller.signal.aborted) { ads.value = result; expireAds() }
    } catch {
      if (!controller.signal.aborted) ads.value = []
    } finally {
      if (!controller.signal.aborted) loading.value = false
    }
  }, { immediate: true, flush: 'sync' })
  onScopeDispose(() => { stop(); request?.abort(); clearExpiry() })
  return { ads, loading }
}
